/**
 * §9.13 Billing tab, customer arm — live twin of the prototype
 * `CustomerBillingView`, rendering `BillingCustomerArmView` off the ENG-5428
 * read model instead of a mock scene. Layout, section order and viewer rules
 * are the prototype's; the deltas are all as-built:
 *
 *  - ONE `periods` list carries both funding modes (the prototype's separate
 *    credit-draw / invoice arrays collapsed onto it), each row frozen in its
 *    own denomination. Displayed newest-first; the wire serves oldest-first.
 *  - The §6.6 funding switch is DERIVED — there is no `funding_switched_at` on
 *    the wire. A switched engagement is one whose live funding path is USD
 *    while its history holds credits rows; the boundary dates come from the
 *    frozen rows themselves (last credits `periodEnd`, first USD
 *    `periodStart`). The TABLE SHAPE is derived separately, from the rows
 *    alone — see `fundingShape`.
 *  - The Receipt column is PRESENCE-keyed on `hostedInvoiceUrl` (ENG-5634).
 *    Its nulls do not fully separate: credits rows never have one, and on a
 *    USD row a null is EITHER an invoice drafted but not yet issued OR a link
 *    withheld from a viewer without `billing:view` (the api gates the bearer
 *    URL on that permission, not on viewer side). The row cannot tell those
 *    apart — `stripeInvoiceStatus` is deliberately unexposed — and both mean
 *    "no receipt to offer this viewer", so both render a dash. Hiding the
 *    column for the permission case would need a viewer-level signal the read
 *    model does not carry; that is a new ticket, not a tweak here.
 *  - No "Payment method on file" section — the read model carries no source
 *    for it. Its settings link-out survives on the Credit-usage section.
 *  - The failed-ACH retry callout is gone with ACH itself (ENG-5427 Won't Do);
 *    FAILED rows carry reason captions instead (`periodStatus.tsx`).
 *  - The bracket line renders the server's `bracketPosition` (index and hour
 *    bounds only — never a rate). Metrics are the server's own
 *    `hoursConsumed`/`hoursRemaining`, never re-derived client-side.
 */
import { Link } from 'react-router';

import { EngagementBillingPeriodStatus, EngagementFundingMethod } from '@/__generated__/graphql';
import { formatCreditsText, safeUrl } from '@/utils';

import { formatDate, formatPeriod } from '../../components/EngagementHub/format';
import Section from '../Section';
import { formatEngagementMoney, formatHours } from '../money';
import { UNKNOWN_PARTY, partyIdentity } from '../parties';
import type { BillingCustomerArmView, BillingPeriodRow, EngagementView } from '../viewModel';
import ChargeHistory, { type ChargeBoundary, type ChargeRow } from './ChargeHistory';
import { PeriodStatusCell } from './periodStatus';

interface CustomerBillingArmProps {
  arm: BillingCustomerArmView;
  view: EngagementView;
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-on-surface-muted subtitle-2">{label}</div>
      <div className="mt-4 text-on-surface hd-5">{value}</div>
    </div>
  );
}

const isCreditsRow = (row: BillingPeriodRow) =>
  row.fundingMethod === EngagementFundingMethod.Credits;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `periodEnd` is EXCLUSIVE (schema: "Exclusive end of the period this row
 * bills"), so the day it names is the first day NOT billed. Every date shown
 * to a customer is the last day that WAS — otherwise adjacent rows both claim
 * the boundary day, and the switch banner dates "through" to the same day the
 * divider calls the switch.
 */
function lastBilledDay(exclusiveEnd: string): string {
  return new Date(new Date(exclusiveEnd).getTime() - DAY_MS).toISOString();
}

/**
 * Two independent questions the one `periods` list answers:
 *
 *  - `wide` — does the table need the uniform mixed-denomination column set?
 *    `ChargeHistory` derives its headers from row 0 alone, so ANY list mixing
 *    denominations must use one column set, whichever direction the funding
 *    moved. That set trades the date columns for `Funding`; it keeps
 *    `Receipt`, which is an affordance rather than a detail — dropping it
 *    would leave a switched engagement's customer no route to their invoice —
 *    but not `Paid on`, because a settle date without the `Billed` claim date
 *    beside it reads as the only date the row has.
 *  - `switchedToUsd` — is this the §6.6 ratchet? That one is directional
 *    (credits history under a live USD arm) and owns the banner and divider
 *    copy, which would be false on a mixed list that isn't a switch.
 */
function fundingShape(arm: BillingCustomerArmView): { wide: boolean; switchedToUsd: boolean } {
  const credits = arm.periods.filter(isCreditsRow).length;
  return {
    wide: credits > 0 && credits < arm.periods.length,
    switchedToUsd: arm.fundingMethod !== EngagementFundingMethod.Credits && credits > 0,
  };
}

/** Row amount in ITS OWN denomination — frozen at close, never converted. */
function amountCell(row: BillingPeriodRow): React.ReactNode {
  // Credits carry their unit (prototype §9.13): in the switched table the
  // Amount column mixes denominations, so a bare number reads as broken next
  // to a $ figure.
  const formatted = isCreditsRow(row)
    ? row.creditAmountCents !== null
      ? formatCreditsText(row.creditAmountCents)
      : '—'
    : row.amountCents !== null
      ? formatEngagementMoney(row.amountCents, row.amountCurrency)
      : '—';
  // A SETTLED zero draw is billed work whose cost rolled into the cumulative
  // basis — say so, or a real charge reads as a free period. Only SETTLED: on
  // any other status a zero is an absence of billing, and the caption would
  // promise a later draw that never comes.
  const rolledForward =
    isCreditsRow(row) &&
    row.creditAmountCents === 0 &&
    row.status === EngagementBillingPeriodStatus.Settled;
  if (!rolledForward) return formatted;
  return (
    <span className="flex flex-col gap-2">
      <span>{formatted}</span>
      <span className="max-w-[200px] text-on-surface-muted body-2">
        Cost rolled into a later period&rsquo;s draw.
      </span>
    </span>
  );
}

/**
 * PRESENCE-keyed, never derived from `invoiced`: `hostedInvoiceUrl` is a
 * bearer link the api gates on `billing:view`, so it is null on a credits row,
 * on a USD row whose invoice is drafted but not yet issued, AND for a
 * customer-side viewer outside the funding account's owning team. The last
 * two are indistinguishable from the row and mean the same thing here — no
 * receipt to offer this viewer — so both get the dash. Rendering an error or a
 * "pending" caption would assert a cause the row cannot support.
 *
 * The wire value goes through `safeUrl` before it reaches the `href`. Stripe
 * only ever mints https links, but React renders whatever scheme it is handed,
 * so a `javascript:` URL arriving from the api would execute on click. A
 * rejected scheme falls into the same dash branch — there is still no receipt
 * to offer, and a link that cannot be trusted is worse than none.
 *
 * `period` names the row in the accessible name: several issued periods would
 * otherwise give a screen-reader user N links all reading "View invoice" with
 * nothing to tell them apart. A row with no period to name keeps the bare
 * label rather than announce "for —".
 */
function receiptCell(row: BillingPeriodRow, period: string | null): React.ReactNode {
  const href = row.hostedInvoiceUrl ? safeUrl(row.hostedInvoiceUrl) : '';
  if (!href) return '—';
  // Keeps its hover underline where the ENG-5737 sweep removed every other one
  // in this tree. `components/Link` underlines the external branch only, so
  // exempting this Stripe-hosted invoice is what makes the sweep consistent
  // rather than an exception to it.
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={period ? `View invoice for ${period}` : undefined}
      className="text-brand-primary hover:underline"
    >
      View invoice
    </a>
  );
}

/**
 * §6.6 — one newest-first history. On a switched engagement the rows
 * interleave naturally (both modes share the one list) and a Funding column
 * plus the boundary divider make the transition legible; single-mode
 * engagements keep the prototype's leaner column sets.
 */
function chargeHistory(
  arm: BillingCustomerArmView,
  wide: boolean,
  switchedToUsd: boolean
): { rows: ChargeRow[]; boundary?: ChargeBoundary } {
  const newestFirst = [...arm.periods].reverse();

  const rows: ChargeRow[] = newestFirst.map((row) => {
    const periodLabel =
      row.periodStart && row.periodEnd
        ? formatPeriod(row.periodStart, lastBilledDay(row.periodEnd))
        : null;
    const period = periodLabel ?? '—';
    const hours = formatHours(row.hoursConfirmed);
    const status = <PeriodStatusCell row={row} />;
    if (wide) {
      return {
        key: row.id,
        cells: [
          ['Period', period],
          ['Funding', isCreditsRow(row) ? 'Credits' : 'USD'],
          ['Hours confirmed', hours],
          ['Amount', amountCell(row)],
          ['Status', status],
          ['Receipt', receiptCell(row, periodLabel)],
        ],
      };
    }
    if (isCreditsRow(row)) {
      return {
        key: row.id,
        cells: [
          ['Period', period],
          ['Hours confirmed', hours],
          ['Credits drawn', amountCell(row)],
          ['Status', status],
        ],
      };
    }
    return {
      key: row.id,
      cells: [
        // `closedAt` is claim time — when we BILLED. `settledAt` is when the
        // money landed: the same tick on the credit path, but hours-to-days
        // later behind a shortfall retry and net-X days later here. Two
        // columns because they are two facts; neither relabels the other.
        ['Billed', row.closedAt ? formatDate(row.closedAt) : '—'],
        ['Paid on', row.settledAt ? formatDate(row.settledAt) : '—'],
        ['Period', period],
        ['Hours confirmed', hours],
        ['Amount', amountCell(row)],
        ['Status', status],
        ['Receipt', receiptCell(row, periodLabel)],
      ],
    };
  });

  if (!switchedToUsd) return { rows };
  const firstCredit = newestFirst.find(isCreditsRow);
  const firstUsdRow = arm.periods.find((row) => !isCreditsRow(row));
  const boundary: ChargeBoundary | undefined = firstCredit
    ? {
        beforeKey: firstCredit.id,
        // `periodStart` is INCLUSIVE — the first USD day is the switch day.
        label: firstUsdRow?.periodStart
          ? `Switched to USD invoicing on ${formatDate(firstUsdRow.periodStart)}`
          : 'Switched to USD invoicing',
      }
    : undefined;
  return { rows, boundary };
}

/** Draft copy (flag for Brian): the server gives index + hour bounds, no bracket names. */
function bracketLine(arm: BillingCustomerArmView): string | null {
  const bracket = arm.bracketPosition;
  if (!bracket) return null;
  const cumulative =
    bracket.cumulativeHours !== null
      ? `${formatHours(bracket.cumulativeHours)} confirmed to date`
      : null;
  const ahead = bracket.finalBracket
    ? 'no further brackets'
    : bracket.hoursToNext !== null
      ? `${formatHours(bracket.hoursToNext)} to the next bracket`
      : null;
  const detail = [cumulative, ahead].filter(Boolean).join(', ');
  return `Currently in bracket ${bracket.index}${detail ? ` — ${detail}` : ''}`;
}

/** Draft copy (flag for Brian): the funding banner's one line. */
function fundingLine(arm: BillingCustomerArmView, switchedToUsd: boolean): string {
  const mechanism = arm.chargeMechanism ? ` · ${arm.chargeMechanism}` : '';
  if (switchedToUsd) {
    // The "through" date is the last credits row's last BILLED day — the day
    // credit billing actually stopped covering, not the exclusive bound.
    const lastCreditEnd = [...arm.periods].reverse().find(isCreditsRow)?.periodEnd ?? null;
    const through = lastCreditEnd ? formatDate(lastBilledDay(lastCreditEnd)) : 'the switch';
    return `Credits-funded through ${through}, now USD invoiced${mechanism}`;
  }
  if (arm.fundingMethod === EngagementFundingMethod.Credits) return 'Credits-funded';
  return `USD invoiced${mechanism}`;
}

export default function CustomerBillingArm({ arm, view }: CustomerBillingArmProps): JSX.Element {
  const isCredits = arm.fundingMethod === EngagementFundingMethod.Credits;
  const { wide, switchedToUsd } = fundingShape(arm);
  const organization = view.project?.organizationName ?? null;
  const groupId = view.project?.groupId ?? null;

  const approverId = view.participants?.find((p) => p.role === 'governance_approver')?.userId;
  // Through `partyIdentity`, so the line names people the way every other
  // surface does: an account-level approver on no project is named by
  // `customerIdentities` (ENG-5750). One nothing names hides the line rather
  // than printing "A participant".
  const approver = approverId ? partyIdentity(view, '')(approverId) : null;
  const approverName = approver && approver !== UNKNOWN_PARTY ? approver.name : null;

  const mode = fundingLine(arm, switchedToUsd);
  const bracket = bracketLine(arm);
  const history = chargeHistory(arm, wide, switchedToUsd);

  return (
    <div className="flex flex-col gap-24">
      <Section title="Funding source">
        <div className="flex flex-col gap-8">
          <span className="text-on-surface body-1">
            {organization ? `${organization} · ${mode}` : mode}
          </span>
          {view.requiresGovernance && approverName ? (
            <span className="text-on-surface-muted body-2">
              Governance approver: {approverName}
            </span>
          ) : null}
        </div>
      </Section>

      {/* §9.13 / D7 — live credit metrics for credit-funded engagements only.
          A switched engagement is USD now; its credit consumption is history,
          visible in the charge history below. */}
      {isCredits ? (
        <Section title="Credit usage">
          <div className="grid grid-cols-1 gap-24 md:grid-cols-3">
            <Metric
              label="Credit balance remaining"
              value={
                // Null is USD-arm or a viewer without billing:view on the
                // account — absence, never "$0" (§6.3 money rule).
                arm.creditBalanceCents !== null ? formatCreditsText(arm.creditBalanceCents) : '—'
              }
            />
            <Metric label="Hours consumed" value={formatHours(arm.hoursConsumed)} />
            <Metric label="Hours remaining within NTE" value={formatHours(arm.hoursRemaining)} />
          </div>
          {bracket ? <p className="mt-16 text-on-surface body-2">{bracket}</p> : null}
          {groupId ? (
            <div className="mt-8">
              <Link to={`/team/${groupId}/settings/billing`} className="text-brand-primary body-2">
                View full account in billing settings
              </Link>
            </div>
          ) : null}
        </Section>
      ) : null}

      <Section title="Charge history">
        <ChargeHistory rows={history.rows} boundary={history.boundary} />
        {history.rows.length === 0 ? (
          <p className="text-on-surface-muted body-2">
            No periods closed yet — charges appear here at each period close.
          </p>
        ) : null}
      </Section>
    </div>
  );
}
